import { useEffect } from 'react';

import type { ConnectionListEndpoint } from '@core/types/types.endpoint.connection';
import type { GoogleConnectEndpoint } from '@core/types/types.endpoint.google';
import { Alert, AlertDescription } from '@frontend/components/ui/alert';
import { Button } from '@frontend/components/ui/button';
import { Card, CardContent } from '@frontend/components/ui/card';
import { SettingsNativeConnectionRow } from '@frontend/Settings/components/SettingsNativeConnectionRow';
import { SettingsAccountType } from '@frontend/Settings/SettingsAccountType';
import { SettingsAnswering } from '@frontend/Settings/SettingsAnswering';
import { SettingsCategories } from '@frontend/Settings/SettingsCategories';
import { SettingsChannels } from '@frontend/Settings/SettingsChannels';
import { SettingsCustomLabels } from '@frontend/Settings/SettingsCustomLabels';
import { SettingsDocumenting } from '@frontend/Settings/SettingsDocumenting';
import { SettingsPermissions } from '@frontend/Settings/SettingsPermissions';
import { useApi } from '@frontend/shared/hooks/shared.hook.useApi';
import { useFlag } from '@frontend/shared/hooks/shared.hook.useFlag';
import { useIsAdmin } from '@frontend/shared/hooks/shared.hook.useIsAdmin';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';
import { InfoIcon } from 'lucide-react';

export function SettingsPage() {
  const isAdmin = useIsAdmin();
  const googleDriveEnabled = useFlag('google_drive');

  const { data: connectionData, refetch: refetchConnections } =
    useApi<ConnectionListEndpoint>('/connection/list', {});

  const connectGoogle = useSubmitter(async () => {
    const [, res] = await callApi<GoogleConnectEndpoint>('/google/connect', {
      local: window.location.host === 'localhost:3000' ? '1' : undefined,
      // No ?success=integration — we go straight into the file picker instead
      // of showing the generic "Integration connected" modal.
      redirectTo: '/settings',
    });
    if (res?.url) {
      window.location.href = res.url;
    }
  });

  useEffect(() => {
    const tId = setTimeout(() => {
      refetchConnections();
    }, 5000);
    return () => clearTimeout(tId);
  }, [refetchConnections]);

  return (
    <>
      {!isAdmin && (
        <Alert className="mb-6 max-w-[600px]">
          <InfoIcon />
          <AlertDescription>
            You are viewing settings in read-only mode. Only administrators can
            edit settings.
          </AlertDescription>
        </Alert>
      )}
      <SettingsAccountType isReadOnly={!isAdmin} />
      <SettingsCategories isReadOnly={!isAdmin} />
      <Card className="mt-12 max-w-[600px]">
        <CardContent>
          <h2 className="text-lg font-semibold">Integrations</h2>
          <p className="mt-2 text-xs font-medium text-gray-500">
            Answer questions in Slack with data from:
          </p>
          {connectionData?.connections.map((c) => (
            <SettingsNativeConnectionRow
              key={c.connectionId}
              connection={c}
              refetch={refetchConnections}
              isReadOnly={!isAdmin}
            />
          ))}
          {googleDriveEnabled && (
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                className="mt-2"
                disabled={!isAdmin || connectGoogle.isSubmitting}
                onClick={() => connectGoogle.submit()}
              >
                <img
                  src="/assets/services/gdrive.png"
                  className="mr-2 size-4"
                  alt=""
                />
                {connectGoogle.isSubmitting
                  ? 'Loading...'
                  : 'Connect Google Drive'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <SettingsChannels isReadOnly={!isAdmin} />
      <SettingsAnswering isReadOnly={!isAdmin} />
      <SettingsCustomLabels isReadOnly={!isAdmin} />
      <SettingsDocumenting isReadOnly={!isAdmin} />
      <SettingsPermissions isReadOnly={!isAdmin} />
    </>
  );
}
